// @flow
import React, { useContext, useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { get, throttle } from 'lodash';

import { windowWrapper } from '@clubmed/utils';

import {
  changeDepartureCity as changeCity,
  setInitialProductPrices,
} from 'Store/faceting/facetingSearch.action';
import {
  setProductComparisonSelectionMode,
  toggleProductSelected,
} from 'Store/faceting/productComparison.action';
import { pricesRequestAction, childcareRequestAction } from 'Store/faceting/searchRequest.action';
import { startLoading } from 'Store/global-loader/globalLoader.action';
import {
  activeFacetsSelector,
  filteredProductsSelector,
  getFiltersSelector,
  recommandedProductsSelector,
} from 'Store/faceting/faceting.selector';
import {
  getDepartureCity,
  getDepartureDate,
  getTripDuration,
} from 'Store/trip-criteria/tripCriteria.selector';
import { DEFAULT_ADULTS_NB } from 'Store/trip-information/adults/constants';
import { DEFAULT_DEPARTURE_CITY } from 'Store/trip-criteria/departure-city/constants';
import { DEFAULT_TRIP_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import * as routeBuilder from 'Routes/routeBuilder';
import { MAX_NUMBER_OF_SELECTED_PRODUCTS, ACTIVITIES_FACET_ID } from 'Utils/faceting/constants';
import { isSeller, sendFacetsToCxO } from 'Utils/seller/sellerUtils';
import { getUrlWithUnpersistedData } from 'Utils/faceting/unpersistedFacetUtils';
import { getDepartureDateDisplayValue } from 'Utils/faceting/facetingUtils';
import ReactAppContext from 'Context/ReactAppContext';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import {
  SearchResults,
  type Props as SearchResultsProps,
} from 'Components/widgets/facet-search-results/search-results/SearchResults.jsx';
import { SearchNoResults } from 'Components/widgets/facet-search-results/search-no-results/SearchNoResults.jsx';
import { SearchResultsPlaceholder } from 'Components/widgets/facet-search-results/search-results-placeholder/SearchResultsPlaceholder.jsx';
import { t } from 'Src/i18n';
import { Title } from 'Components/frameworks/title/Title';
import Head from 'next/head';
import { SearchResultsTitle } from './search-results-title/SearchResultsTitle';

export type Props = {
  i18n?: CMSContent,
  initialProducts?: $ReadOnlyArray<OfferProduct>,
  isProductComparisonModeAuthorized?: boolean,
  offer?: Offer,
  resultsHasSeeMore?: boolean,
  resultsSeeMore?: $PropertyType<SearchResultsProps, 'seeMore'>,
  searchOptions?: FacetingSearchOptions,
  shouldSetDefaultDepartureCityOnCompare?: boolean,
  virtualizedGrid?: boolean,
  isSynchronousGrid?: boolean,
};

function mapStateToProps(state: ReduxState) {
  const {
    faceting: {
      childcareAvailabilities = [],
      isDateRequired,
      isLoading,
      isPriceLoading = false,
      isSelectionModeEnabled,
      noResults = false,
      pertimmInterpretations,
      prices = [],
      productsSelectedForComparison,
      query,
    },
    tripInformation: { adults = [], kids },
  } = state;
  return {
    activeFacets: activeFacetsSelector(state),
    adultsNumber: adults.length || DEFAULT_ADULTS_NB,
    childcareAvailabilities,
    departureCity: getDepartureCity(state),
    departureDate: getDepartureDate(state),
    duration: getTripDuration(state),
    filteredProducts: filteredProductsSelector(state),
    recommandedProducts: recommandedProductsSelector(state),
    filters: getFiltersSelector(state),
    isDateRequired,
    isLoading,
    isPriceLoading,
    isProductComparisonSelectionModeEnabled: isSelectionModeEnabled,
    kids,
    noResults,
    pertimmInterpretations,
    prices,
    productsSelectedForComparison,
    query,
  };
}

export const FacetSearchResults = ({
  i18n,
  initialProducts,
  isProductComparisonModeAuthorized = false,
  isSynchronousGrid = false,
  resultsHasSeeMore,
  resultsSeeMore,
  searchOptions = {},
  shouldSetDefaultDepartureCityOnCompare = false,
  virtualizedGrid,
  offer = {},
}: Props) => {
  const {
    activeFacets,
    adultsNumber,
    childcareAvailabilities,
    departureCity,
    departureDate,
    duration,
    filteredProducts,
    filters,
    isDateRequired,
    isLoading,
    isPriceLoading,
    isProductComparisonSelectionModeEnabled,
    kids,
    noResults,
    pertimmInterpretations,
    prices,
    productsSelectedForComparison,
    query,
    recommandedProducts,
  } = useSelector(mapStateToProps);
  const appContext = useContext(ReactAppContext);
  const filteredProductsRef = useRef(null);
  const dispatch = useDispatch();

  // This state, the updateCompareButtonStickyStatus function and the first effect should be moved to SearchCompare
  const [isProductsTopInViewport, setProductsTopInViewport] = useState(true);
  const [isProductsBottomInViewport, setProductsBottomInViewport] = useState(false);

  const updateCompareButtonStickyStatus = throttle(() => {
    if (filteredProducts.length > 0 && filteredProductsRef.current) {
      const searchResultsWrapperClientRect = filteredProductsRef.current.getBoundingClientRect();
      const clientHeight = windowWrapper.getClientHeight();

      setProductsTopInViewport(searchResultsWrapperClientRect.top <= clientHeight);
      setProductsBottomInViewport(searchResultsWrapperClientRect.bottom <= clientHeight);
    }
  }, 200);

  useEffect(() => {
    if (isProductComparisonModeAuthorized) {
      document.removeEventListener('scroll', updateCompareButtonStickyStatus, false);
      document.addEventListener('scroll', updateCompareButtonStickyStatus, false);
    }

    if (initialProducts && initialProducts.length > 0) {
      dispatch(setInitialProductPrices(initialProducts));
    }
  }, []);

  let productIds = [];
  productIds = useMemo(
    () =>
      (filteredProducts.length !== 0 ? filteredProducts : recommandedProducts).map(
        product => product.id,
      ),
    [filteredProducts.length !== 0 ? filteredProducts : recommandedProducts],
  );

  useEffect(
    () => {
      if (productIds && productIds.length > 0) {
        const { minDate, maxDate } = pertimmInterpretations;
        const { boutique, defaultDepartureCity, offerId, offerName } = searchOptions;
        dispatch(
          pricesRequestAction({
            appContext,
            criteria: {
              boutique: boutique,
              defaultDepartureCity,
              departureDate,
              duration,
              filters,
              maxDate,
              minDate,
              offerId,
              offerName,
              productIds,
              query: query ? query.label : '',
            },
          }),
        );
      }

      /**
       * Component should fetch childcare availabilities
       * if availabilities don't exist yet
       * and if there are eligeable kids
       */
      const childcareProductIds = childcareAvailabilities.map(
        childcareAvailability => childcareAvailability.productId,
      );
      const productsWithoutChildcareAvailabilities = productIds.filter(
        id => !childcareProductIds.includes(id),
      );
      const kidsWithChildcare = kids.filter(kid => kid.childcareIncluded && kid.birthdate);
      if (
        productsWithoutChildcareAvailabilities.length > 0 &&
        kidsWithChildcare.length > 0 &&
        departureDate
      ) {
        dispatch(
          childcareRequestAction({
            appContext,
            criteria: {
              departureDate,
              duration: duration || DEFAULT_TRIP_DURATION,
              kids: kidsWithChildcare,
              productIds,
            },
          }),
        );
      }
    },
    [departureCity, productIds, kids],
  );

  useEffect(
    () => {
      if (!isDateRequired && !departureDate) {
        dispatch(setProductComparisonSelectionMode(false));
      }
    },
    [isDateRequired],
  );

  const computeFacetsToCXOParams = () => {
    const destinations = filteredProducts
      .filter(product => productsSelectedForComparison.indexOf(get(product, 'id')) !== -1)
      .map(destination => get(destination, 'name'));

    const activities = activeFacets[ACTIVITIES_FACET_ID]
      ? Object.keys(activeFacets[ACTIVITIES_FACET_ID])
      : null;
    return [
      getDepartureDateDisplayValue(departureDate),
      departureCity,
      destinations && destinations.length > 0 ? destinations.join(' / ') : null,
      adultsNumber,
      kids.length,
      activities && activities.length > 0 ? activities.join(' / ') : null, // activities
      adultsNumber + kids.length, // participants
      kids.some(kid => kid.childcareIncluded),
    ];
  };

  const onClickCompareButton = () => {
    dispatch(startLoading());
    if (isSeller(appContext)) {
      sendFacetsToCxO(computeFacetsToCXOParams());
    }
  };

  const toggleProductComparisonSelectionMode = () => {
    dispatch(setProductComparisonSelectionMode(!isProductComparisonSelectionModeEnabled));

    if (
      shouldSetDefaultDepartureCityOnCompare &&
      !departureCity &&
      !isProductComparisonSelectionModeEnabled
    ) {
      dispatch(changeCity(DEFAULT_DEPARTURE_CITY));
    }
  };

  const getCompareUrl = () => {
    const url = routeBuilder.urlPattern('comparator');

    return getUrlWithUnpersistedData({
      url,
      query,
      productIdsToCompare: productsSelectedForComparison,
      departureCity,
      departureDate,
      duration,
    });
  };

  const toggleProductSelectedForComparison = () => {
    if (isProductComparisonSelectionModeEnabled) {
      return productId => dispatch(toggleProductSelected(productId));
    }
  };

  const hasDepartureDate = !!departureDate;
  const hasDepartureCity = !!departureCity;
  const resultsCount = filteredProducts.length;
  const shouldDisplayResults = resultsCount > 0 && !isLoading && !noResults;
  const comparisonUrl = getCompareUrl();

  if (noResults && !isLoading) {
    const title = <Title theme="title-brand-9">{t('remote.noResult.recommendation.title')}</Title>;

    return (
      <Fragment>
        <Head>
          <title>{i18n?.remote?.noResult?.zeroResult}</title>
        </Head>
        <SiteContainer>
          <div data-testid="facet-search-no-results">
            <SearchNoResults
              title={t('remote.noResult.explanation')}
              explanation={t('remote.noResult.updateCriteria')}
            />
          </div>
          {recommandedProducts.length !== 0 ? (
            <div data-testid="facet-search-recommanded-results">
              <SearchResults
                childcareAvailabilities={childcareAvailabilities}
                className="pb-24 lg:pb-10"
                comparisonUrl={comparisonUrl}
                config={i18n}
                displayValidationButton={!isSeller(appContext)}
                hasSeeMore={false}
                isPriceLoading={isPriceLoading}
                isProductsBottomInViewport={isProductsBottomInViewport}
                isProductComparisonSelectionModeEnabled={false}
                isProductComparisonModeAuthorized={false}
                isProductsTopInViewport={isProductsTopInViewport}
                isSynchronousGrid={isSynchronousGrid}
                offer={offer}
                onClickCompareButton={onClickCompareButton}
                prices={prices}
                resorts={recommandedProducts}
                title={title}
                toggleProductComparisonSelectionMode={toggleProductComparisonSelectionMode}
                virtualizedGrid={virtualizedGrid}
              />
            </div>
          ) : (
            <div />
          )}
        </SiteContainer>
      </Fragment>
    );
  }

  if (isLoading || !resultsCount) {
    return (
      <div data-testid="facet-search-results">
        <SiteContainer>
          <SearchResultsPlaceholder nbItems={8} />
        </SiteContainer>
        )
      </div>
    );
  }

  const title = (
    <SearchResultsTitle
      pluralLabel={t('faceting.nbResortsThatMatchYourCriteria')}
      resultsCount={filteredProducts.length}
      singularLabel={t('faceting.oneResortThatMatchYourCriteria')}
    />
  );
  return (
    <div ref={filteredProductsRef} data-testid="facet-search-results">
      {shouldDisplayResults && (
        <SearchResults
          className="pb-24 lg:pb-10"
          title={title}
          config={i18n}
          offer={offer}
          resorts={filteredProducts}
          prices={prices}
          isPriceLoading={isPriceLoading}
          childcareAvailabilities={childcareAvailabilities}
          isProductComparisonSelectionModeEnabled={
            isProductComparisonSelectionModeEnabled && resultsCount > 0
          }
          productsSelectedForComparison={productsSelectedForComparison}
          maxSelectableItems={MAX_NUMBER_OF_SELECTED_PRODUCTS}
          toggleProductSelectedForComparison={toggleProductSelectedForComparison()}
          toggleProductComparisonSelectionMode={toggleProductComparisonSelectionMode}
          displayValidationButton={!isSeller(appContext)}
          hasSeeMore={resultsHasSeeMore}
          seeMore={resultsSeeMore}
          isSynchronousGrid={isSynchronousGrid}
          virtualizedGrid={virtualizedGrid}
          isProductComparisonModeAuthorized={isProductComparisonModeAuthorized}
          onClickCompareButton={onClickCompareButton}
          comparisonUrl={comparisonUrl}
          isProductsBottomInViewport={isProductsBottomInViewport}
          isProductsTopInViewport={isProductsTopInViewport}
          hasDepartureDate={hasDepartureDate}
          hasDepartureCity={hasDepartureCity}
        />
      )}
    </div>
  );
};
